import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { Box, Divider, Flex, Loader, Stack, Text, Title } from "@mantine/core";

import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import {
  useGetEvent,
  useGetEventSetupProgress,
  usePatchSetupSkipped,
} from "../api/event";
import SetupChecklist from "../components/Setup/SetupChecklist";
import SetupSidePanel from "../components/Setup/SetupSidePanel";

export default function Setup() {
  const { user } = useContext(AuthContext);
  const isAdmin =
    user?.role === "sysAdmin" ||
    (user?.role === "wsAdmin" && !user?.config?.isTicketAdmin);
  const eventId = isAdmin ? getCurrentWorkspace(user)?.eventId : null;
  const { data, isPending, isError, error } = useGetEventSetupProgress(eventId);
  const { data: event, isFetching: isEventFetching } = useGetEvent(eventId);
  const { mutate: patchSkipped, isPending: isSaving } =
    usePatchSetupSkipped(eventId);

  if (!isAdmin) return <Navigate to="/404" />;
  if (isPending) return <Loader mt={10} />;
  if (isError) return <Text c="red">{error.message}</Text>;

  const skipped = event?.config?.setupSkipped ?? [];
  const onToggleSkip = (key) =>
    patchSkipped({
      setupSkipped: skipped.includes(key)
        ? skipped.filter((item) => item !== key)
        : [...skipped, key],
    });

  return (
    <Flex direction={{ base: "column", lg: "row" }} gap="lg">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Stack gap="md">
          <div>
            <Title order={2}>Set up {event?.name ?? "your event"}</Title>
            <Text c="dimmed">
              Work through these steps to get your event into the app.
            </Text>
          </div>
          <SetupChecklist
            progress={data}
            skipped={skipped}
            onToggleSkip={onToggleSkip}
            isSaving={isSaving || isEventFetching || !event}
          />
        </Stack>
      </Box>
      <Divider orientation="vertical" visibleFrom="lg" />
      <Box w={{ base: "100%", lg: 320 }} style={{ flexShrink: 0 }}>
        <SetupSidePanel event={data.event} counts={data.counts} />
      </Box>
    </Flex>
  );
}
